import React, { useEffect, useMemo, useState, useCallback } from "react";
import SimpleTable from "./Tabler/SimpleTable";
import DI from "../../utility/DI";
import { environment } from "../../../Env_data/environment";
import { Tab, Tabs, Typography, IconButton, Tooltip, Box } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { AppContext } from "../../../App";
import { useContext } from "react";
import HomeBoard from "./HomeBoard";
import { FetchContext } from "../SideBar";
import { categoryOptions } from "../../../Constant";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import BillImageModal from "./BillImageModal";

export const getCategoryLabel = (value) => {
  return (
    categoryOptions?.find((obj) => obj?.value === value)?.label ??
    value ??
    "Uncategorized"
  );
};

// Base columns without renderCell for description (will be added in component with modal handler)
const baseColumns = [
  { field: "id", headerName: "ID", width: 75, filterable: false },
  { field: "title", headerName: "Title", width: 200 },
  {
    field: "category",
    headerName: "Category",
    width: 200,
    valueGetter: (value) => getCategoryLabel(value),
  },
  { field: "brand", headerName: "Brand", width: 150 },
  { field: "weight", headerName: "Weight", width: 140 },
  { field: "quantity", headerName: "Quantity", width: 140 },
  { field: "price", headerName: "Price", width: 140, type: "number" },
  { field: "total", headerName: "Total", width: 150, type: "number" },
  { field: "expense_detail", headerName: "Description", width: 280 },
  { field: "paid_by", headerName: "Paid by", width: 250 },
  {
    field: "expense_time",
    headerName: "Date",
    width: 200,
    valueFormatter: (value) => {
      // Format DATETIME "2025-03-18 13:33:00" to "Tue 18-Mar-2025"
      if (!value) return "";
      try {
        const date = new Date(value);
        const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleDateString("en-US", { month: "short" });
        const year = date.getFullYear();
        return `${dayName} ${day}-${month}-${year}`;
      } catch {
        return value;
      }
    },
  },
];

function Home(props) {
  const [loading, setLoading] = React.useState(false);
  const { setState } = useContext(AppContext);
  const { refresh, setRefresh } = useContext(FetchContext);
  const {
    di: { urls, session, success, error, GET },
  } = props;
  const [currentHome, setCurrentHome] = React.useState(
    session?.homes?.[0]?.id ?? null
  );

  // Bill image modal state
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const handleOpenBillModal = useCallback((expense) => {
    setSelectedExpense(expense);
    setBillModalOpen(true);
  }, []);

  const handleCloseBillModal = useCallback(() => {
    setBillModalOpen(false);
    setSelectedExpense(null);
  }, []);

  const handleDelete = async (id) => {
    await GET(urls.get.delete_home_expense, {
      transaction_id: id,
    })
      .then((res) => {
        if (res?.success) {
          success(res?.message ?? "Successfully deleted");
        } else {
          error(res?.message ?? "Error deleting transaction");
        }
      })
      .catch((er) => {
        error("Oops..! session out. Refresh the page");
      })
      .finally(() => {
        setRefresh((prev) => !prev);
      });
  };
  const columnsWithDelete = useMemo(() => {
    // Build a fresh copy of columns each time
    const cols = baseColumns.map((col) => {
      // Override the description column to include bill doc icon inline
      if (col.field === "expense_detail") {
        return {
          ...col,
          renderCell: (params) => {
            const billCount = parseInt(params.row.bill_count) || 0;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {billCount > 0 && (
                  <Tooltip
                    title={`${billCount} bill image${billCount > 1 ? "s" : ""} — click to view`}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenBillModal(params.row);
                      }}
                      sx={{
                        color: "#1a237e",
                        backgroundColor: "rgba(26, 35, 126, 0.08)",
                        minWidth: 28,
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "rgba(26, 35, 126, 0.18)",
                          transform: "scale(1.12)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.82rem",
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            );
          },
        };
      }
      return col;
    });

    // Add delete column at the beginning
    if (!cols.find((el) => el.field === "delete"))
      cols.splice(0, 0, {
        field: "delete",
        headerName: "Delete",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="delete"
            size="small"
            color="error"
            disabled={session.id !== params.row.created_by}
            onClick={() => {
              const confirm = window.confirm(
                `Sure to DELETE "${params.row.title}" transaction with id: "${params.row.id}"?`
              );
              if (confirm) handleDelete(params.row.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      });

    return cols;
  }, []);
  const handleChange = (event, newValue) => {
    setCurrentHome(newValue);
    setRefresh((prev) => !prev);
  };
  const getHomes = async () => {
    if (!session?.id) {
      error("User ID cannot be null");
      return;
    }
    setLoading(true);
    await GET(urls.get.get_homes, {
      user_id: session?.id,
    })
      .then((res) => {
        if (res?.success) {
          // success(res?.message ?? "Homes fetched successfully");
          if (session != undefined)
            setState((prev) => ({
              ...prev,
              session: { ...prev.session, homes: res.homes },
            }));
        } else {
          error(res?.message ?? "Error fetching homes");
        }
        setLoading(false);
      })
      .catch((er) => {
        error("Oops..! session out. Refresh the page");
        setLoading(false);
      });
  };

  useEffect(() => {
    getHomes();
  }, []);

  return (
    <div className="App">
      {currentHome === null ? (
        <Typography
          variant="p"
          component="p"
          sx={{ marginLeft: 1, marginX: 1 }}
          gutterBottom
          align="center"
        >
          {
            "You have not added any home yet. Please add a home to manage expenses."
          }
        </Typography>
      ) : null}
      <HomeBoard
        currentHome={currentHome}
        key={"dashboardHome"}
        refresh={refresh}
      />
      <br />
      {currentHome && (
        <>
          <Tabs
            value={currentHome}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
          >
            {session?.homes?.map(({ id, name }) => (
              <Tab
                icon={<HomeIcon />}
                iconPosition="start"
                label={name.toUpperCase()}
                value={id}
              />
            ))}
          </Tabs>
          <SimpleTable
            key={currentHome}
            mode="server"
            apiEndpoint={environment.BASE_URL + urls.get.get_home_expenses}
            docTitle="Home expense" //for excel title
            columns={columnsWithDelete}
            onRowClick={(data) => console.log("row clicked", data)} //on row click
            isRowSelectable={(params) => {
              //   let id = params.id;
              //   if (typeof id ==="string") {
              //     id = parseInt(id);
              //   }
              //   return id % 2 !== 0;
              return true;
            }} //callbak for row selectable or not callback
            onCellClick={(data) => {
              console.log({ data });
            }}
            getRowClassName={(params) => {
              //   if (params.row.id === 2) {
              //     return "custom_row";
              //   }
              //   return "";
            }} //with this callback we return classname for row by conditons
            defaultSelection={(params) => {
              //   let id = params.id;
              //   if (typeof id ==="string") {
              //     id = parseInt(id);
              //   }
              //   return id % 2 === 0;
            }}
            session={session}
            currentHome={currentHome}
          />
        </>
      )}

      {/* Bill Image Viewer Modal */}
      <BillImageModal
        open={billModalOpen}
        onClose={handleCloseBillModal}
        expense={selectedExpense}
      />
    </div>
  );
}

export default DI(Home);
